import { StateCreator } from 'zustand';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  timestamp: number;
}

export interface UISlice {
  // Tracking редактируемых элементов
  editingItems: Set<string>;

  // Cache timestamps для background prefetching
  cacheTimestamps: Record<string, number>;

  // Notifications
  notifications: Notification[];

  // Actions
  startEditing: (id: string) => void;
  stopEditing: (id: string) => void;
  isEditing: (id: string) => boolean;

  addNotification: (type: Notification['type'], message: string) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  setCacheTimestamp: (entity: string, timestamp: number) => void;
  shouldRefetch: (entity: string, maxAge?: number) => boolean;
}

export const createUISlice: StateCreator<UISlice> = (set, get) => ({
  editingItems: new Set(),
  cacheTimestamps: {},
  notifications: [],

  startEditing: (id: string) => {
    set((state) => ({
      editingItems: new Set([...state.editingItems, id]),
    }));
  },

  stopEditing: (id: string) => {
    set((state) => {
      const newSet = new Set(state.editingItems);
      newSet.delete(id);
      return { editingItems: newSet };
    });
  },

  isEditing: (id: string) => {
    return get().editingItems.has(id);
  },

  addNotification: (type: Notification['type'], message: string) => {
    const notification: Notification = {
      id: `${Date.now()}-${Math.random()}`,
      type,
      message,
      timestamp: Date.now(),
    };

    set((state) => ({
      notifications: [...state.notifications, notification],
    }));

    // Автоматически удаляем через 5 секунд
    setTimeout(() => {
      get().removeNotification(notification.id);
    }, 5000);
  },

  removeNotification: (id: string) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearNotifications: () => {
    set({ notifications: [] });
  },

  setCacheTimestamp: (entity: string, timestamp: number) => {
    set((state) => ({
      cacheTimestamps: { ...state.cacheTimestamps, [entity]: timestamp },
    }));
  },

  shouldRefetch: (entity: string, maxAge: number = 60000) => {
    const lastFetch = get().cacheTimestamps[entity];
    return !lastFetch || Date.now() - lastFetch > maxAge;
  },
});
